<template>
  <div class="page-container">
    <Card class="search-card" dis-hover>
      <Form inline :label-width="120" :model="search" @submit.native.prevent>
        <FormItem label="应用名称">
          <Input v-model.trim="search.apply_name" placeholder="请输入应用名称" clearable style="width: 200px" />
        </FormItem>
        <FormItem label="变动类型">
          <Select v-model="search.change_type" clearable placeholder="全部" style="width: 140px">
            <Option v-for="item in changeTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</Option>
          </Select>
        </FormItem>
        <FormItem label="任务状态">
          <Select v-model="search.task_status" clearable placeholder="全部" style="width: 140px">
            <Option v-for="item in taskStatusOptions" :key="item.value" :value="item.value">{{ item.label }}</Option>
          </Select>
        </FormItem>
        <FormItem>
          <Button type="primary" icon="ios-search" @click="handleSearch">查询</Button>
          <Button style="margin-left: 8px" @click="resetSearch">重置</Button>
        </FormItem>
      </Form>
    </Card>

    <Card class="table-card" dis-hover>
      <Alert type="info" show-icon>
        资源点增减记录提供明细追溯：正数表示增加资源点，负数表示扣减。AI任务相关记录会显示任务状态，输入和输出资源点会合并显示为一个整体任务。
      </Alert>
      <Spin fix v-if="loading" />
      <Table :columns="columns" :data="list.data" border stripe :width="null">
        <template slot="applyName" slot-scope="{ row }">
          <span>{{ row.apply_name || '云端账号' }}</span>
        </template>
        <template slot="changeType" slot-scope="{ row }">
          <Tag :color="changeTypeColor(row.change_type)">{{ changeTypeText(row.change_type) }}</Tag>
        </template>
        <template slot="points" slot-scope="{ row }">
          <!-- 如果是AI任务记录（有task_status），显示输入和输出资源点合计（使用卡片样式） -->
          <div v-if="row.task_status !== null && row.task_status !== undefined && row.task_status !== ''" class="task-points-card">
            <div class="task-points-header">
              <Icon type="ios-cube" size="16" />
              <span class="task-points-title">AI任务资源点</span>
            </div>
            <div class="task-points-body">
              <div class="points-item">
                <span class="points-label">输入资源点：</span>
                <span class="points-value text-danger">-{{ getInputPoints(row) }}</span>
              </div>
              <div v-if="row.has_output" class="points-item">
                <span class="points-label">输出资源点：</span>
                <span class="points-value text-danger">-{{ row.output_points_formatted || row.output_points || 0 }}</span>
              </div>
              <div v-else class="points-item">
                <span class="points-label text-muted">输出资源点：</span>
                <span class="points-value text-muted">待扣除</span>
              </div>
              <div class="points-item total-item">
                <span class="points-label">合计消耗：</span>
                <span class="points-value text-danger bold-text">-{{ getTotalPoints(row) }}</span>
              </div>
            </div>
          </div>
          <!-- 普通记录，显示原来的points_delta -->
          <span v-else :class="row.points_delta >= 0 ? 'text-success' : 'text-danger'">
            {{ row.points_delta >= 0 ? '+' : '' }}{{ row.points_delta }}
          </span>
        </template>
        <template slot="available" slot-scope="{ row }">
          <div>
            <p>操作前：{{ row.available_before }}</p>
            <p>操作后：<span class="bold-text">{{ row.available_after }}</span></p>
          </div>
        </template>
        <template slot="taskType" slot-scope="{ row }">
          <Tag v-if="row.ai_type" color="blue" size="small">{{ row.ai_type_text || row.ai_type }}</Tag>
          <span v-else class="text-muted">-</span>
        </template>
        <template slot="taskStatus" slot-scope="{ row }">
          <Tag v-if="row.task_status !== null && row.task_status !== undefined && row.task_status !== ''" 
               :color="taskStatusColor(row.task_status)">
            {{ row.task_status_text || getTaskStatusText(row.task_status) }}
          </Tag>
          <span v-else class="text-muted">-</span>
        </template>
        <template slot="orderId" slot-scope="{ row }">
          <span v-if="row.order_id" class="order-id-link">{{ formatOrderNo(row.order_id) }}</span>
          <span v-else class="text-muted">-</span>
        </template>
        <template slot="context" slot-scope="{ row }">
          <div class="context-detail">
            <div v-if="row.biz_id" class="detail-item">
              <span class="detail-label">业务ID：</span>
              <Tooltip :content="row.biz_id" placement="top" transfer>
                <span class="detail-value">{{ row.biz_id | truncate(30) }}</span>
              </Tooltip>
            </div>
            <div v-if="row.ai_conversation_id || row.ai_conversation_id_from_context" class="detail-item">
              <span class="detail-label">对话ID：</span>
              <Tooltip :content="row.ai_conversation_id || row.ai_conversation_id_from_context" placement="top" transfer>
                <span class="detail-value">{{ (row.ai_conversation_id || row.ai_conversation_id_from_context) | truncate(25) }}</span>
              </Tooltip>
            </div>
            <div v-if="row.completed_at" class="detail-item">
              <span class="detail-label">完成时间：</span>
              <span class="detail-value">{{ row.completed_at }}</span>
            </div>
            <div v-else-if="row.timeout_at" class="detail-item">
              <span class="detail-label">超时时间：</span>
              <span class="detail-value">{{ row.timeout_at }}</span>
            </div>
          </div>
        </template>
      </Table>
      <Page
        v-if="list.total > 0"
        class="pagination"
        :total="list.total"
        :current="search.page"
        :page-size="search.pageSize"
        show-total
        show-elevator
        show-sizer
        @on-change="handlePageChange"
        @on-page-size-change="handlePageSizeChange"
      />
    </Card>
  </div>
</template>

<script>
export default {
  name: 'AiPointsUsageLog',
  filters: {
    truncate (value, length = 20) {
      if (!value) return ''
      if (value.length <= length) return value
      return value.substring(0, length) + '...'
    }
  },
  data () {
    return {
      loading: false,
      search: {
        apply_name: '',
        change_type: '',
        task_status: '',
        page: 1,
        pageSize: 10
      },
      list: {
        data: [],
        total: 0
      },
      changeTypeOptions: [
        { label: '全部', value: '' },
        { label: '扣减', value: 1 },
        { label: '回滚', value: 2 },
        { label: '到期扣除', value: 3 },
        { label: '人工调整', value: 4 },
        { label: '充值入账', value: 5 }
      ],
      columns: [
        { title: '应用', slot: 'applyName', minWidth: 140 },
        { title: '变动类型', slot: 'changeType', width: 110, align: 'center' },
        { title: '任务类型', slot: 'taskType', width: 110, align: 'center' },
        { title: '任务状态', slot: 'taskStatus', width: 110, align: 'center' },
        { title: '变动数量', slot: 'points', minWidth: 180, align: 'center' },
        { title: '额度变化', slot: 'available', minWidth: 160 },
        { title: '订单号', slot: 'orderId', width: 130, align: 'center' },
        { title: '详细信息', slot: 'context', minWidth: 240 },
        { title: '记录时间', key: 'created_at', width: 170 }
      ],
      taskStatusOptions: [
        { label: '全部', value: '' },
        { label: '待处理', value: 0 },
        { label: '处理中', value: 1 },
        { label: '成功', value: 2 },
        { label: '失败', value: 3 },
        { label: '超时', value: 4 },
        { label: '非任务记录', value: 'null' }
      ]
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.loading = true
      const payload = { search: { ...this.search } }
      this.requestApi('/adm/get_usage_log', payload).then(res => {
        this.loading = false
        if (res.status === 1) {
          this.list = res.data || { data: [], total: 0 }
        } else {
          this.list = { data: [], total: 0 }
          this.alertErr(res.msg || '获取消耗明细失败')
        }
      })
    },
    handleSearch () {
      this.search.page = 1
      this.fetchData()
    },
    resetSearch () {
      this.search = {
        apply_name: '',
        change_type: '',
        task_status: '',
        page: 1,
        pageSize: this.search.pageSize
      }
      this.fetchData()
    },
    handlePageChange (page) {
      this.search.page = page
      this.fetchData()
    },
    handlePageSizeChange (size) {
      this.search.pageSize = size
      this.handleSearch()
    },
    changeTypeText (type) {
      const map = {
        1: '扣减',
        2: '回滚',
        3: '到期扣除',
        4: '人工调整',
        5: '充值入账'
      }
      return map[type] || '未知'
    },
    changeTypeColor (type) {
      const map = {
        1: 'error',
        2: 'success',
        3: 'warning',
        4: 'primary',
        5: 'success'
      }
      return map[type] || 'default'
    },
    getTaskStatusText (status) {
      const map = {
        0: '待处理',
        1: '处理中',
        2: '成功',
        3: '失败',
        4: '超时'
      }
      return map[status] || '未知'
    },
    taskStatusColor (status) {
      const map = {
        0: 'default',
        1: 'blue', // 处理中改为蓝色，更明显
        2: 'success',
        3: 'error',
        4: 'warning'
      }
      return map[status] || 'default'
    },
    getTaskStatusStyle () {
      return {}
    },
    formatOrderNo (orderId) {
      // 格式化订单号显示（order_id本身就是订单号）
      if (!orderId) return '-'
      return orderId.toString()
    },
    getInputPoints (row) {
      // 获取输入资源点：优先使用input_points，如果没有则从points_delta计算（处理中状态）
      if (row.input_points !== null && row.input_points !== undefined && row.input_points > 0) {
        return row.input_points_formatted || row.input_points
      }
      // 如果没有input_points，说明可能是旧数据或数据异常，尝试从points_delta计算
      // 如果output_points=0，说明只扣除了输入资源点，points_delta就是输入资源点
      if (row.output_points === null || row.output_points === 0) {
        const inputPoints = Math.abs(row.points_delta || 0)
        return this.formatPoints(inputPoints)
      }
      // 如果output_points>0，说明已经扣除了输出资源点，需要计算输入资源点
      // points_delta = -(input_points + output_points)，所以 input_points = abs(points_delta) - output_points
      if (row.output_points > 0) {
        const totalPoints = Math.abs(row.points_delta || 0)
        const outputPoints = row.output_points || 0
        const inputPoints = totalPoints - outputPoints
        return this.formatPoints(inputPoints > 0 ? inputPoints : 0)
      }
      return '0'
    },
    getTotalPoints (row) {
      // 获取总资源点：优先使用total_points，如果没有则计算
      if (row.total_points !== null && row.total_points !== undefined && row.total_points > 0) {
        return row.total_points_formatted || row.total_points
      }
      // 如果total_points为0，但input_points有值，说明可能是回滚后的记录
      // 回滚后points_delta为0，但input_points保留用于显示
      if (row.input_points && row.input_points > 0 && (row.points_delta === 0 || row.points_delta === null)) {
        // 回滚后的记录：显示input_points（表示原本要扣除的输入资源点）
        return this.formatPoints(row.input_points)
      }
      // 如果没有total_points，从points_delta计算（取绝对值）
      const totalPoints = Math.abs(row.points_delta || 0)
      return this.formatPoints(totalPoints)
    },
    formatPoints (points) {
      // 格式化资源点显示，保留小数位
      if (!points && points !== 0) return '0'
      const num = parseFloat(points)
      // 判断是否为整数
      const isInteger = Math.abs(num - Math.round(num)) < 0.001
      if (isInteger) {
        // 整数：只添加千分位
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      } else {
        // 小数：保留2位小数并添加千分位
        return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 16px;
  background: #f5f7f9;
  min-height: calc(100vh - 64px);
}

.search-card {
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.search-card .ivu-card-body {
  padding: 16px 20px;
}

.search-card .ivu-form-item {
  margin-bottom: 0;
  margin-right: 16px;
}

.search-card .ivu-form-item:last-child {
  margin-right: 0;
}

.table-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-card .ivu-card-body {
  padding: 20px 24px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #e8eaec;
}
.text-success {
  color: #19be6b;
  font-weight: 600;
}
.text-danger {
  color: #ed4014;
  font-weight: 600;
}
.bold-text {
  font-weight: 600;
}
.text-muted {
  color: #c5c8ce;
  font-style: italic;
}

/* 任务资源点卡片样式 */
.task-points-card {
  border: 1px solid #e8eaec;
  border-radius: 4px;
  background: #f8f9fa;
  overflow: hidden;
}

.task-points-header {
  background: #2d8cf0;
  color: #fff;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
}

.task-points-title {
  font-size: 12px;
}

.task-points-body {
  padding: 10px;
}

.points-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
}

.points-item:last-child {
  margin-bottom: 0;
}

.points-label {
  color: #515a6e;
  font-weight: 500;
}

.points-value {
  font-weight: 600;
}

.total-item {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e8eaec;
}

.total-item .points-label {
  font-weight: 600;
  color: #2d8cf0;
}

/* 详细信息样式 */
.context-detail {
  text-align: left;
}

.detail-item {
  margin-bottom: 8px;
  line-height: 1.6;
  font-size: 13px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #808695;
  font-weight: 500;
  margin-right: 6px;
}

.detail-value {
  color: #515a6e;
}

/* 关联订单号样式 */
.order-id-link {
  color: #2d8cf0;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}

.order-id-link:hover {
  color: #5cadff;
  text-decoration: underline;
}

.task-points-detail {
  text-align: left;
}

.task-points-detail > div {
  margin-bottom: 4px;
  line-height: 1.5;
}

.task-points-detail > div:last-child {
  margin-bottom: 0;
}

.task-points-detail .total-points {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #e8eaec;
  font-weight: 500;
}

/* 处理中状态动画 */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* 详细信息样式优化 */
.context-detail {
  text-align: left;
  font-size: 13px;
  padding: 4px 0;
}

.detail-item {
  margin-bottom: 6px;
  line-height: 1.6;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #808695;
  font-weight: 500;
  margin-right: 6px;
  display: inline-block;
  min-width: 70px;
}

.detail-value {
  color: #515a6e;
  word-break: break-all;
  cursor: pointer;
}
</style>

